import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { useId } from 'react';

interface FieldWrapperProps {
  label: string;
  htmlFor: string;
  error?: string | undefined;
  required?: boolean;
  children: ReactNode;
}

function FieldWrapper({
  label,
  htmlFor,
  error,
  required,
  children,
}: FieldWrapperProps): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={htmlFor} className="text-sm font-medium text-slate-700">
        {label}
        {required ? <span className="ml-0.5 text-rose-600">*</span> : null}
      </label>
      {children}
      {error ? <p className="text-xs text-rose-600">{error}</p> : null}
    </div>
  );
}

const CONTROL_CLASS =
  'rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-50 disabled:text-slate-500';

/** Shared input styling for custom controls that need to match `TextField`/`SelectField`. */
export const FIELD_CONTROL_CLASS = CONTROL_CLASS;

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string | undefined;
}

export function TextField({ label, error, required, ...props }: TextFieldProps): JSX.Element {
  const id = useId();
  return (
    <FieldWrapper label={label} htmlFor={id} error={error} required={required}>
      <input id={id} className={CONTROL_CLASS} {...props} />
    </FieldWrapper>
  );
}

interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string | undefined;
}

export function TextAreaField({
  label,
  error,
  required,
  ...props
}: TextAreaFieldProps): JSX.Element {
  const id = useId();
  return (
    <FieldWrapper label={label} htmlFor={id} error={error} required={required}>
      <textarea id={id} className={CONTROL_CLASS} {...props} />
    </FieldWrapper>
  );
}

interface CheckboxFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
}

export function CheckboxField({ label, ...props }: CheckboxFieldProps): JSX.Element {
  const id = useId();
  return (
    <div className="flex items-center gap-2">
      <input
        id={id}
        type="checkbox"
        className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
        {...props}
      />
      <label htmlFor={id} className="text-sm text-slate-700">
        {label}
      </label>
    </div>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  error?: string | undefined;
  options: SelectOption[];
  placeholder?: string;
}

export function SelectField({
  label,
  error,
  required,
  options,
  placeholder,
  ...props
}: SelectFieldProps): JSX.Element {
  const id = useId();
  return (
    <FieldWrapper label={label} htmlFor={id} error={error} required={required}>
      <select id={id} className={CONTROL_CLASS} {...props}>
        {placeholder ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldWrapper>
  );
}
